import type {
  SerializedTableCellNode,
  SerializedTableNode,
  SerializedTableRowNode,
} from "@lexical/table";
import { TableCellHeaderStates, TableCellNode, TableNode, TableRowNode } from "@lexical/table";
import type {
  SerializedEditorState,
  SerializedElementNode,
  SerializedLexicalNode,
  SerializedParagraphNode,
  SerializedTextNode,
} from "lexical";
import { cloneDeep, isEmpty } from "lodash-es";
import { getImageDimensions } from "../../utils/image-dimensions";
import type { SerializedImageNode } from "../message/editor/nodes/image-node";
import type { StyleableColumnConfig } from "../message/editor/utils/use-styleable-columns";
import type { EmailHeaderProperties } from "./types/email-header-type";

export function makeEmptySerializedState(
  body?: (SerializedElementNode | SerializedLexicalNode)[],
): SerializedEditorState {
  return {
    root: {
      type: "root",
      format: "",
      indent: 0,
      version: 1,
      children: body ?? [],
      direction: "ltr",
    },
  };
}

function headerTable(children: {
  [k: string]: SerializedLexicalNode[];
}): SerializedTableNode | undefined {
  if (isEmpty(children)) {
    return undefined;
  }
  return {
    type: TableNode.getType(),
    children: [
      {
        type: TableRowNode.getType(),
        children: [
          {
            type: TableCellNode.getType(),
            children: children["left"],
            version: 1,
            direction: "ltr",
            format: "left",
            width: undefined,
            indent: 0,
            headerState: TableCellHeaderStates.NO_STATUS,
          },
          {
            type: TableCellNode.getType(),
            children: children["center"],
            version: 1,
            direction: "ltr",
            format: "left",
            width: undefined,
            indent: 0,
            headerState: TableCellHeaderStates.NO_STATUS,
          },
          {
            type: TableCellNode.getType(),
            children: children["right"],
            version: 1,
            direction: "ltr",
            format: "left",
            width: undefined,
            indent: 0,
            headerState: TableCellHeaderStates.NO_STATUS,
          },
        ] as SerializedTableCellNode[],
        version: 1,
        direction: "ltr",
        format: "left",
        indent: 0,
        // Lexical type is wrong this is fully supported.
        height: undefined,
      } as SerializedTableRowNode,
    ],
    version: 1,
    direction: "ltr",
    format: "left",
    indent: 0,
  };
}

export async function makeHeaderSerializedState(
  header: EmailHeaderProperties | null,
  handleImageType?: (url: string | undefined) => Promise<string>,
): Promise<SerializedEditorState | null> {
  if (header === null || !(header.logo || header.text)) return null;

  const text: { [k: string]: SerializedParagraphNode[] } =
    header.text && header.text.align
      ? {
          [header.text.align]: [
            {
              type: "paragraph",
              format: header.text.align,
              textStyle: "",
              textFormat: 0,
              indent: 0,
              version: 1,
              children: [
                {
                  mode: "normal",
                  text: header.text.value,
                  format: 0,
                  style: `${header.text.color ? `color: ${header.text.color};` : ""}`,
                  type: "text",
                  version: 1,
                  detail: 0,
                } as SerializedTextNode,
              ],
              direction: null,
            },
          ],
        }
      : {};

  const logoSrc = handleImageType
    ? await handleImageType(header?.logo?.src)
    : (header?.logo?.src ?? "");
  const logoAspect = header.logo ? await getImageDimensions(logoSrc) : { width: 1, height: 1 };

  const logo: { [k: string]: SerializedImageNode[] } = header.logo
    ? {
        [header.logo.align]: [
          {
            url: logoSrc,
            type: "image",
            version: 2,
            format: header.logo.align,
            proportionOfMaxWidth: header.logo.proportionOfMaxWidth ?? 1,
            aspect: logoAspect.width / logoAspect.height,
          },
        ],
      }
    : {};

  const logoTable =
    header.logo?.version === 2 ? logo[header.logo.align]![0] : headerTable(cloneDeep(logo));
  const textTable =
    header.text && header.text.align && header.logo?.version === 2
      ? text[header.text?.align]![0]
      : headerTable(text);

  return makeEmptySerializedState([
    ...(logoTable ? [logoTable] : []),
    ...(textTable ? [textTable] : []),
  ]);
}

export const makeColumnPaddings = (
  columnConfig?: StyleableColumnConfig | null,
): {
  left: number;
  right: number;
  top: number;
  bottom: number;
} => {
  if (!columnConfig) {
    return {
      left: 0,
      right: 0,
      top: 0,
      bottom: 0,
    };
  }

  const paddingNumbers = columnConfig.column_individual_padding
    ? {
        left: columnConfig.column_padding_left ?? 0,
        right: columnConfig.column_padding_right ?? 0,
        top: columnConfig.column_padding_top ?? 0,
        bottom: columnConfig.column_padding_bottom ?? 0,
      }
    : {
        left: columnConfig.column_padding ?? 0,
        right: columnConfig.column_padding ?? 0,
        top: columnConfig.column_padding ?? 0,
        bottom: columnConfig.column_padding ?? 0,
      };

  return {
    left: paddingNumbers.left,
    right: paddingNumbers.right,
    top: paddingNumbers.top,
    bottom: paddingNumbers.bottom,
  };
};

export const makePx = (value?: number): string => `${value || 0}px`;

export const makeRenderPaddingPxs = (
  columnConfig?: StyleableColumnConfig | null,
): {
  paddingLeft: string;
  paddingRight: string;
  paddingTop: string;
  paddingBottom: string;
} => {
  const { left, right, top, bottom } = makeColumnPaddings(columnConfig);
  return {
    paddingLeft: makePx(left),
    paddingRight: makePx(right),
    paddingTop: makePx(top),
    paddingBottom: makePx(bottom),
  };
};
